import type { Lead, LeadSource } from '@up2stay/up2promo-api-client';
import { LeadSource as LeadSourceEnum } from '@up2stay/up2promo-api-client';
import {
  Button,
  Drawer,
  DrawerContent,
  Input,
  Switch,
  cn,
} from '@up2stay/up2promo-ui';
import {
  Archive,
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  ScanLine,
  Search,
  Tag,
  UserCheck,
  X,
} from 'lucide-react';
import { useId, useMemo, useState } from 'react';

import {
  applyLeadsListFilters,
  clearLeadsSheetFilters,
  countActiveLeadsSheetFilters,
  EMPTY_LEADS_LIST_FILTERS,
  type LeadsCreatedWithinPreset,
  type LeadsListFilterCriteria,
} from '@/core/components/leads/lead-filter-normalize';
import {
  LEADS_MOBILE_SHEET_SORT_OPTIONS,
  type LeadsListSortOption,
} from '@/core/components/leads/leads-list-sort';

const FILTERABLE_SOURCES: LeadSource[] = [
  LeadSourceEnum.PUBLIC_FORM,
  LeadSourceEnum.MANUAL,
  LeadSourceEnum.BUSINESS_CARD,
];

const CREATED_WITHIN_ORDER: LeadsCreatedWithinPreset[] = [
  'all',
  'last_day',
  'last_week',
  'last_month',
];

type SheetPage =
  | 'root'
  | 'events'
  | 'assignees'
  | 'tags'
  | 'sources'
  | 'period';

type UserFilterOption = { userId: string; label: string };
type EventFilterOption = { id: string; label: string };

export type LeadsMobileFilterSheetLabels = {
  filter: string;
  cancel: string;
  reset: string;
  sectionSort: string;
  sectionFilters: string;
  allValues: string;
  showResults: string;
  showResultsNone: string;
  searchEvent: string;
  searchAssignee: string;
  searchTag: string;
  sectionEvents: string;
  sectionAssignee: string;
  sectionTags: string;
  sectionSource: string;
  sectionPeriod: string;
  showArchived: string;
  noEventsToFilter: string;
  noPeopleToFilter: string;
  noTagsToFilter: string;
  periodAll: string;
  periodLastDay: string;
  periodLastWeek: string;
  periodLastMonth: string;
  sourceLabels: Record<string, string>;
  sortChipLabels: Record<LeadsListSortOption, string>;
};

type LeadsMobileFilterSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: LeadsListFilterCriteria;
  onChange: (next: LeadsListFilterCriteria) => void;
  listSort: LeadsListSortOption;
  onListSortChange: (next: LeadsListSortOption) => void;
  includeArchived: boolean;
  onIncludeArchivedChange: (next: boolean) => void;
  leads: Lead[];
  searchQuery: string;
  assigneeOptions: UserFilterOption[];
  tagOptions: string[];
  eventOptions: EventFilterOption[] | null;
  labels: LeadsMobileFilterSheetLabels;
};

function toggleInList<T>(list: T[], item: T): T[] {
  return list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
}

function summarizeMulti(
  selectedLabels: string[],
  allLabel: string,
): { text: string; set: boolean } {
  if (selectedLabels.length === 0) return { text: allLabel, set: false };
  if (selectedLabels.length === 1) {
    return { text: selectedLabels[0] ?? allLabel, set: true };
  }
  return { text: String(selectedLabels.length), set: true };
}

export function LeadsMobileFilterSheet({
  open,
  onOpenChange,
  value,
  onChange,
  listSort,
  onListSortChange,
  includeArchived,
  onIncludeArchivedChange,
  leads,
  searchQuery,
  assigneeOptions,
  tagOptions,
  eventOptions,
  labels,
}: LeadsMobileFilterSheetProps) {
  const [page, setPage] = useState<SheetPage>('root');
  const [draft, setDraft] = useState<LeadsListFilterCriteria>(value);
  const [draftSort, setDraftSort] = useState<LeadsListSortOption>(listSort);
  const [draftArchived, setDraftArchived] = useState(includeArchived);
  const [optionQuery, setOptionQuery] = useState('');

  const periodLabels: Record<LeadsCreatedWithinPreset, string> = {
    all: labels.periodAll,
    last_day: labels.periodLastDay,
    last_week: labels.periodLastWeek,
    last_month: labels.periodLastMonth,
  };

  const previewCount = useMemo(() => {
    const combined: LeadsListFilterCriteria = {
      ...draft,
      statuses: value.statuses,
    };
    return applyLeadsListFilters(leads, combined, searchQuery).length;
  }, [draft, value.statuses, leads, searchQuery]);

  const eventSummary = summarizeMulti(
    (eventOptions ?? [])
      .filter((o) => draft.eventIds.includes(o.id))
      .map((o) => o.label),
    labels.allValues,
  );
  const assigneeSummary = summarizeMulti(
    assigneeOptions
      .filter((o) => draft.assigneeUserIds.includes(o.userId))
      .map((o) => o.label),
    labels.allValues,
  );
  const tagSummary = summarizeMulti(
    draft.tags.filter((t) => tagOptions.includes(t)),
    labels.allValues,
  );
  const sourceSummary = summarizeMulti(
    draft.sources.map((s) => labels.sourceLabels[s] ?? s),
    labels.allValues,
  );
  const periodSet = draft.createdWithin !== 'all';
  const periodSummary = periodLabels[draft.createdWithin];

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleReset = () => {
    setDraft({
      ...clearLeadsSheetFilters(EMPTY_LEADS_LIST_FILTERS),
      statuses: value.statuses,
    });
    setDraftArchived(false);
  };

  const handleApply = () => {
    onChange({
      ...draft,
      statuses: value.statuses,
    });
    onListSortChange(draftSort);
    onIncludeArchivedChange(draftArchived);
    onOpenChange(false);
  };

  const title =
    page === 'root'
      ? labels.filter
      : page === 'events'
        ? labels.sectionEvents
        : page === 'assignees'
          ? labels.sectionAssignee
          : page === 'tags'
            ? labels.sectionTags
            : page === 'sources'
              ? labels.sectionSource
              : labels.sectionPeriod;

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onOpenChange(false);
      }}
    >
      <DrawerContent
        fitContent={false}
        className="flex h-[min(92dvh,720px)] max-h-[min(92dvh,720px)] flex-col"
      >
        <div className="mx-auto mt-1 h-1 w-9 shrink-0 rounded-full bg-[color:var(--surface-border)]" />

        <div className="grid shrink-0 grid-cols-[1fr_auto_1fr] items-center px-4 pb-2 pt-2.5">
          {page === 'root' ? (
            <button
              type="button"
              className="justify-self-start text-[15px] text-[color:var(--ghost-accent-fg)]"
              onClick={handleCancel}
            >
              {labels.cancel}
            </button>
          ) : (
            <button
              type="button"
              className="inline-flex items-center gap-0.5 justify-self-start text-[15px] text-[color:var(--ghost-accent-fg)]"
              onClick={() => {
                setPage('root');
                setOptionQuery('');
              }}
            >
              <ChevronLeft className="size-[18px]" aria-hidden />
              {labels.filter}
            </button>
          )}
          <h2 className="text-center text-[16px] font-bold">{title}</h2>
          {page === 'root' ? (
            <button
              type="button"
              className="justify-self-end text-[15px] text-[color:var(--ghost-accent-fg)]"
              onClick={handleReset}
            >
              {labels.reset}
            </button>
          ) : (
            <span />
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-2">
          {page === 'root' ? (
            <RootPage
              draftSort={draftSort}
              draftArchived={draftArchived}
              labels={labels}
              eventOptions={eventOptions}
              eventSummary={eventSummary}
              assigneeSummary={assigneeSummary}
              tagSummary={tagSummary}
              sourceSummary={sourceSummary}
              periodSummary={periodSummary}
              periodSet={periodSet}
              onSortChange={setDraftSort}
              onArchivedChange={setDraftArchived}
              onOpenPage={setPage}
            />
          ) : (
            <OptionsPage
              page={page}
              draft={draft}
              onChange={setDraft}
              optionQuery={optionQuery}
              onOptionQueryChange={setOptionQuery}
              assigneeOptions={assigneeOptions}
              tagOptions={tagOptions}
              eventOptions={eventOptions}
              labels={labels}
              periodLabels={periodLabels}
            />
          )}
        </div>

        <div className="shrink-0 border-t border-[color:var(--surface-border)] px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3">
          <Button
            type="button"
            size="lg"
            className="h-[50px] w-full rounded-[14px] text-[16px] font-semibold"
            disabled={previewCount === 0}
            variant={previewCount === 0 ? 'secondary' : 'primary'}
            onClick={handleApply}
          >
            {previewCount === 0
              ? labels.showResultsNone
              : labels.showResults.replace('{count}', String(previewCount))}
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function RootPage({
  draftSort,
  draftArchived,
  labels,
  eventOptions,
  eventSummary,
  assigneeSummary,
  tagSummary,
  sourceSummary,
  periodSummary,
  periodSet,
  onSortChange,
  onArchivedChange,
  onOpenPage,
}: {
  draftSort: LeadsListSortOption;
  draftArchived: boolean;
  labels: LeadsMobileFilterSheetLabels;
  eventOptions: EventFilterOption[] | null;
  eventSummary: { text: string; set: boolean };
  assigneeSummary: { text: string; set: boolean };
  tagSummary: { text: string; set: boolean };
  sourceSummary: { text: string; set: boolean };
  periodSummary: string;
  periodSet: boolean;
  onSortChange: (next: LeadsListSortOption) => void;
  onArchivedChange: (next: boolean) => void;
  onOpenPage: (page: SheetPage) => void;
}) {
  const archivedId = useId();
  return (
    <>
      <div className="px-5 pb-1.5 pt-2.5 text-[10.5px] font-semibold tracking-[0.08em] text-muted-foreground">
        {labels.sectionSort}
      </div>
      <div className="flex flex-wrap gap-1.5 px-4">
        {LEADS_MOBILE_SHEET_SORT_OPTIONS.map((option) => {
          const active = draftSort === option;
          return (
            <button
              key={option}
              type="button"
              onClick={() => onSortChange(option)}
              className={cn(
                'inline-flex h-8 items-center rounded-full px-3 text-[13px]',
                active
                  ? 'bg-foreground font-semibold text-background'
                  : 'border border-[color:var(--surface-border)] bg-transparent',
              )}
            >
              {labels.sortChipLabels[option]}
            </button>
          );
        })}
      </div>

      <div className="px-5 pb-1.5 pt-[18px] text-[10.5px] font-semibold tracking-[0.08em] text-muted-foreground">
        {labels.sectionFilters}
      </div>
      <div className="mx-4 overflow-hidden rounded-[var(--radius-card-mobile)] border border-[color:var(--surface-border)]">
        {eventOptions !== null ? (
          <FilterNavRow
            icon={Calendar}
            label={labels.sectionEvents}
            value={eventSummary.text}
            valueSet={eventSummary.set}
            onClick={() => onOpenPage('events')}
          />
        ) : null}
        <FilterNavRow
          icon={UserCheck}
          label={labels.sectionAssignee}
          value={assigneeSummary.text}
          valueSet={assigneeSummary.set}
          onClick={() => onOpenPage('assignees')}
          bordered={eventOptions !== null}
        />
        <FilterNavRow
          icon={Tag}
          label={labels.sectionTags}
          value={tagSummary.text}
          valueSet={tagSummary.set}
          onClick={() => onOpenPage('tags')}
          bordered
        />
        <FilterNavRow
          icon={ScanLine}
          label={labels.sectionSource}
          value={sourceSummary.text}
          valueSet={sourceSummary.set}
          onClick={() => onOpenPage('sources')}
          bordered
        />
        <FilterNavRow
          icon={Clock}
          label={labels.sectionPeriod}
          value={periodSummary}
          valueSet={periodSet}
          onClick={() => onOpenPage('period')}
          bordered
        />
        <div className="flex min-h-[50px] items-center gap-3 border-t border-[color:var(--surface-border)] px-3.5">
          <Archive
            className="size-4 shrink-0 text-muted-foreground"
            aria-hidden
          />
          <label
            htmlFor={archivedId}
            className="flex-1 cursor-pointer text-[14px]"
          >
            {labels.showArchived}
          </label>
          <Switch
            id={archivedId}
            checked={draftArchived}
            onCheckedChange={(c) => onArchivedChange(!!c)}
          />
        </div>
      </div>
    </>
  );
}

function FilterNavRow({
  icon: Icon,
  label,
  value,
  valueSet,
  onClick,
  bordered = false,
}: {
  icon: typeof Calendar;
  label: string;
  value: string;
  valueSet: boolean;
  onClick: () => void;
  bordered?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex min-h-[50px] w-full items-center gap-3 px-3.5 text-left',
        bordered && 'border-t border-[color:var(--surface-border)]',
      )}
    >
      <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="flex-1 text-[14px]">{label}</span>
      <span
        className={cn(
          'max-w-[45%] truncate text-[13px]',
          valueSet ? 'font-semibold text-foreground' : 'text-muted-foreground',
        )}
      >
        {value}
      </span>
      <ChevronRight
        className="size-[15px] shrink-0 text-muted-foreground"
        aria-hidden
      />
    </button>
  );
}

function OptionsPage({
  page,
  draft,
  onChange,
  optionQuery,
  onOptionQueryChange,
  assigneeOptions,
  tagOptions,
  eventOptions,
  labels,
  periodLabels,
}: {
  page: Exclude<SheetPage, 'root'>;
  draft: LeadsListFilterCriteria;
  onChange: (next: LeadsListFilterCriteria) => void;
  optionQuery: string;
  onOptionQueryChange: (q: string) => void;
  assigneeOptions: UserFilterOption[];
  tagOptions: string[];
  eventOptions: EventFilterOption[] | null;
  labels: LeadsMobileFilterSheetLabels;
  periodLabels: Record<LeadsCreatedWithinPreset, string>;
}) {
  const q = optionQuery.trim().toLowerCase();

  if (page === 'period') {
    return (
      <div className="flex flex-col px-2 pt-2">
        {CREATED_WITHIN_ORDER.map((preset) => {
          const selected = draft.createdWithin === preset;
          return (
            <button
              key={preset}
              type="button"
              onClick={() => onChange({ ...draft, createdWithin: preset })}
              className={cn(
                'flex min-h-14 items-center gap-3 rounded-xl px-3',
                selected && 'bg-[color:var(--surface-2)]',
              )}
            >
              <SelectionMark selected={selected} />
              <span className="flex-1 text-left text-[15px]">
                {periodLabels[preset]}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  const searchPlaceholder =
    page === 'events'
      ? labels.searchEvent
      : page === 'assignees'
        ? labels.searchAssignee
        : page === 'tags'
          ? labels.searchTag
          : labels.sectionSource;

  const showSearch = page !== 'sources';

  let rows: Array<{
    key: string;
    label: string;
    selected: boolean;
    onToggle: () => void;
    muted?: boolean;
  }> = [];

  if (page === 'events') {
    const opts = eventOptions ?? [];
    rows = opts
      .filter((o) => !q || o.label.toLowerCase().includes(q))
      .map((o) => ({
        key: o.id,
        label: o.label,
        selected: draft.eventIds.includes(o.id),
        onToggle: () =>
          onChange({
            ...draft,
            eventIds: toggleInList(draft.eventIds, o.id),
          }),
      }));
  } else if (page === 'assignees') {
    rows = assigneeOptions
      .filter((o) => !q || o.label.toLowerCase().includes(q))
      .map((o) => ({
        key: o.userId,
        label: o.label,
        selected: draft.assigneeUserIds.includes(o.userId),
        onToggle: () =>
          onChange({
            ...draft,
            assigneeUserIds: toggleInList(draft.assigneeUserIds, o.userId),
          }),
      }));
  } else if (page === 'tags') {
    rows = tagOptions
      .filter((t) => !q || t.toLowerCase().includes(q))
      .map((t) => ({
        key: t,
        label: t,
        selected: draft.tags.includes(t),
        onToggle: () =>
          onChange({
            ...draft,
            tags: toggleInList(draft.tags, t),
          }),
      }));
  } else {
    rows = FILTERABLE_SOURCES.map((source) => ({
      key: source,
      label: labels.sourceLabels[source] ?? source,
      selected: draft.sources.includes(source),
      onToggle: () =>
        onChange({
          ...draft,
          sources: toggleInList(draft.sources, source),
        }),
    }));
  }

  const emptyMessage =
    page === 'events'
      ? labels.noEventsToFilter
      : page === 'assignees'
        ? labels.noPeopleToFilter
        : page === 'tags'
          ? labels.noTagsToFilter
          : null;

  return (
    <div className="flex flex-col gap-2 px-2 pt-2">
      {showSearch ? (
        <div className="relative mx-2 mb-1">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={optionQuery}
            onChange={(e) => onOptionQueryChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-10 rounded-xl border-0 bg-[color:var(--surface-2)] pl-9 shadow-none"
          />
          {optionQuery ? (
            <button
              type="button"
              className="absolute top-1/2 right-2 -translate-y-1/2 rounded-full p-1 text-muted-foreground"
              onClick={() => onOptionQueryChange('')}
              aria-label={labels.cancel}
            >
              <X className="size-3.5" />
            </button>
          ) : null}
        </div>
      ) : null}

      {rows.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">
          {emptyMessage}
        </p>
      ) : (
        rows.map((row) => (
          <button
            key={row.key}
            type="button"
            onClick={row.onToggle}
            className={cn(
              'flex min-h-14 items-center gap-3 rounded-xl px-3',
              row.selected && 'bg-[color:var(--surface-2)]',
            )}
          >
            <SelectionMark selected={row.selected} />
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-left text-[15px]',
                row.muted && 'text-muted-foreground',
              )}
            >
              {row.label}
            </span>
          </button>
        ))
      )}
    </div>
  );
}

function SelectionMark({ selected }: { selected: boolean }) {
  return selected ? (
    <span className="inline-flex size-[22px] shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
      <Check className="size-3.5" aria-hidden />
    </span>
  ) : (
    <span
      className="size-[22px] shrink-0 rounded-md border-[1.5px] border-[color:var(--surface-border)]"
      aria-hidden
    />
  );
}

/** Active sheet-filter chips under the status row (FilterMobile F3). */
export function LeadsActiveFilterChips({
  value,
  onChange,
  includeArchived,
  onIncludeArchivedChange,
  assigneeOptions,
  eventOptions,
  labels,
}: {
  value: LeadsListFilterCriteria;
  onChange: (next: LeadsListFilterCriteria) => void;
  includeArchived: boolean;
  onIncludeArchivedChange: (next: boolean) => void;
  assigneeOptions: UserFilterOption[];
  eventOptions: EventFilterOption[] | null;
  labels: {
    showArchived: string;
    periodLabels: Record<LeadsCreatedWithinPreset, string>;
    sourceLabels: Record<string, string>;
    removeFilter: string;
  };
}) {
  const chips: Array<{ key: string; label: string; onRemove: () => void }> = [];

  for (const id of value.eventIds) {
    const label = eventOptions?.find((o) => o.id === id)?.label ?? id;
    chips.push({
      key: `event:${id}`,
      label,
      onRemove: () =>
        onChange({
          ...value,
          eventIds: value.eventIds.filter((x) => x !== id),
        }),
    });
  }
  for (const userId of value.assigneeUserIds) {
    const label =
      assigneeOptions.find((o) => o.userId === userId)?.label ?? userId;
    chips.push({
      key: `assignee:${userId}`,
      label,
      onRemove: () =>
        onChange({
          ...value,
          assigneeUserIds: value.assigneeUserIds.filter((x) => x !== userId),
        }),
    });
  }
  for (const tag of value.tags) {
    chips.push({
      key: `tag:${tag}`,
      label: tag,
      onRemove: () =>
        onChange({
          ...value,
          tags: value.tags.filter((t) => t !== tag),
        }),
    });
  }
  for (const source of value.sources) {
    chips.push({
      key: `source:${source}`,
      label: labels.sourceLabels[source] ?? source,
      onRemove: () =>
        onChange({
          ...value,
          sources: value.sources.filter((s) => s !== source),
        }),
    });
  }
  if (value.createdWithin !== 'all') {
    chips.push({
      key: 'period',
      label: labels.periodLabels[value.createdWithin],
      onRemove: () => onChange({ ...value, createdWithin: 'all' }),
    });
  }
  if (includeArchived) {
    chips.push({
      key: 'archived',
      label: labels.showArchived,
      onRemove: () => onIncludeArchivedChange(false),
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 px-0.5">
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          onClick={chip.onRemove}
          className="inline-flex h-7 items-center gap-1.5 rounded-lg bg-[color:var(--ghost-accent-bg)] py-0 pr-1.5 pl-2.5 text-[12.5px] font-semibold text-[color:var(--ghost-accent-fg)]"
        >
          <span className="max-w-[12rem] truncate">{chip.label}</span>
          <X className="size-[13px] shrink-0" aria-hidden />
          <span className="sr-only">{labels.removeFilter}</span>
        </button>
      ))}
    </div>
  );
}

export function leadsMobileFilterTriggerClass(active: boolean): string {
  return cn(
    'inline-flex items-center gap-1.5 text-[12px] font-semibold',
    active
      ? 'h-7 rounded-full bg-foreground px-2.5 text-background'
      : 'text-[color:var(--ghost-accent-fg)]',
  );
}

export function leadsMobileFilterActiveCount(
  filters: LeadsListFilterCriteria,
  includeArchived: boolean,
): number {
  return countActiveLeadsSheetFilters(filters, includeArchived);
}
